import { useState } from "react";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { QrCode, Send, Copy, Printer, Loader2 } from "lucide-react";
import logo from "../assets/gfm-logo-square.png";

const CONTACT_LINE = "Questions? Contact the GFM site team — kcockram@gfm-uk.com";

export function ContractorInvitePanel() {
  const [inviteEmail, setInviteEmail] = useState("");
  const [sending, setSending] = useState(false);
  const registerUrl = `${window.location.origin}/contractor/login?register=1`;

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return toast.error("Enter the contractor's email address.");
    setSending(true);
    try {
      await api.post("/contractor-invite", { email: inviteEmail.trim() });
      toast.success(`Invite sent to ${inviteEmail.trim()}`);
      setInviteEmail("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(registerUrl);
    toast.success("Registration link copied");
  };

  const printQR = () => {
    const svg = document.getElementById("invite-qr")?.querySelector("svg");
    if (!svg) return;
    const logoUrl = logo.startsWith("http") ? logo : window.location.origin + logo;
    const w = window.open("", "_blank", "width=720,height=900");
    if (!w) return;
    w.document.write(`<html><head><title>Register as a contractor</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;text-align:center;padding:40px;color:#00355e;min-height:100vh;display:flex;flex-direction:column;align-items:center}
      img.logo{height:80px;margin-bottom:16px}
      h1{font-size:30px;margin:0 0 6px} p{color:#374151;font-size:17px;margin:0 0 4px}
      .qr{margin:24px auto 16px;width:340px} .url{word-break:break-all;font-size:13px;color:#00355e}
      .contact{margin-top:auto;padding-top:24px;font-size:16px;font-weight:600;color:#0d2a4b}</style>
      </head><body>
      <img class="logo" src="${logoUrl}"/>
      <h1>Register as a Contractor</h1>
      <p>Scan this QR code with your phone camera</p>
      <p>to set up your GFM contractor profile</p>
      <div class="qr">${svg.outerHTML}</div>
      <div class="url">${registerUrl}</div>
      <div class="contact">${CONTACT_LINE}</div>
      <script>window.onload=function(){setTimeout(function(){window.print()},400)}</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div className="rounded-md border border-border bg-white p-5 shadow-sm" data-testid="invite-card">
      <h3 className="font-heading text-lg font-bold tracking-tight text-foreground">Invite a contractor to register</h3>
      <p className="mt-1 text-sm text-muted-foreground">Send a registration link by email, or show/print the QR code for contractors to scan and set up their own profile.</p>

      <div className="mt-4 space-y-2">
        <label className="text-sm font-medium">Send invite by email</label>
        <div className="flex gap-2">
          <Input type="email" placeholder="contractor@company.com" value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)} data-testid="invite-email-input" />
          <Button className="bg-brand hover:bg-brand-dark shrink-0" onClick={sendInvite} disabled={sending} data-testid="send-invite-button">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            <span className="ml-2 hidden sm:inline">Send</span>
          </Button>
        </div>
      </div>

      <div className="my-4 flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
        <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
      </div>

      <div className="flex flex-col items-center rounded-lg border border-border bg-secondary/40 p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-brand"><QrCode className="h-4 w-4" /> Scan to register</div>
        <div id="invite-qr" className="mt-3 rounded-md bg-white p-3 shadow-sm" data-testid="invite-qr">
          <QRCode value={registerUrl} size={168} fgColor="#00355e" />
        </div>
        <div className="mt-4 flex w-full gap-2">
          <Button variant="outline" className="flex-1" onClick={copyLink} data-testid="copy-link-button">
            <Copy className="mr-2 h-4 w-4" /> Copy link
          </Button>
          <Button variant="outline" className="flex-1" onClick={printQR} data-testid="print-qr-button">
            <Printer className="mr-2 h-4 w-4" /> Print QR
          </Button>
        </div>
      </div>
    </div>
  );
}
